import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import type { Match } from '@airmail/engine';
import { useCompetition, useSync, useTurnier } from '@/store/TournamentProvider';
import { deleteReport, listReports, type ResultReport } from '@/store/sync';
import { Button } from '@/ui';
import { feldName, matchContext, slotLabel, teamLookup } from '@/lib/teams';
import { useNow } from '@/hooks/useNow';
import { useT } from '@/i18n';
import { MELDEN_AKTIV } from '@/lib/flags';

/**
 * Ergebnismeldungen der Spieler (docs/notes/live-melden.md, docs/notes/reports.md):
 * Auf der Zuschauer-Seite melden die Teams ihr Ergebnis selbst; hier sieht die
 * Turnierleitung die Meldungen und bestätigt sie mit einem Griff — dann läuft
 * eine ganz normale `match.result`-Aktion durchs Protokoll — oder verwirft sie.
 *
 * Die Meldung selbst ändert nie etwas am Turnier. Unplausible Werte gehen
 * trotzdem durch (`force`), die Warnung erscheint als Toast: Die Leitung
 * entscheidet. Meldungen zu Spielen, die nicht mehr laufen, räumt die Leiste
 * still weg. Alle 15 s neu, nur mit Cloud-Sync und Netz.
 */
export function ReportsPanel() {
  // Solange das Melden abgeschaltet ist, gibt es nichts zu holen — und kein Polling
  if (!MELDEN_AKTIV) return null;
  return <ReportsPanelAktiv />;
}

function ReportsPanelAktiv() {
  const t = useT();
  const { tournament, dispatch } = useTurnier();
  const sync = useSync();
  const comp = useCompetition();
  const now = useNow(30_000);
  const [reports, setReports] = useState<ResultReport[]>([]);
  const cfg = sync.cfg;
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;
  const tid = cfg?.tournamentId;

  const load = useCallback(async () => {
    const c = cfgRef.current;
    if (!c || !navigator.onLine) return;
    try {
      setReports(await listReports(c));
    } catch {
      // Meldungen sind Beiwerk — ein Netzfehler darf den Turniertags-Screen nicht stören
    }
  }, []);

  useEffect(() => {
    if (!tid) return;
    void load();
    const iv = window.setInterval(() => document.visibilityState === 'visible' && void load(), 15_000);
    return () => window.clearInterval(iv);
  }, [tid, load]);

  const drop = useCallback(async (rid: string) => {
    setReports((prev) => prev.filter((r) => r.id !== rid));
    const c = cfgRef.current;
    if (!c) return;
    try {
      await deleteReport(c, rid);
    } catch {
      // beim nächsten Laden taucht sie wieder auf — kein Drama
    }
  }, []);

  const allMatches = useMemo(() => comp.phases.flatMap((p) => p.matches), [comp]);
  const rows = useMemo(
    () => reports.map((r) => ({ r, m: allMatches.find((x) => x.id === r.matchId) })).filter((x): x is { r: ResultReport; m: Match } => !!x.m && x.m.status === 'running'),
    [reports, allMatches],
  );

  // Meldungen zu Spielen, die schon ein Ergebnis haben (oder es gar nicht gibt), automatisch verwerfen
  useEffect(() => {
    for (const r of reports) {
      const m = allMatches.find((x) => x.id === r.matchId);
      if (!m || m.status !== 'running') void drop(r.id);
    }
  }, [reports, allMatches, drop]);

  if (!tid || rows.length === 0) return null;

  const lookup = teamLookup(comp);
  const confirm = (r: ResultReport, m: Match) => {
    if (r.a === r.b) return;
    const res = dispatch({ type: 'match.result', matchId: m.id, result: { sets: [{ a: r.a, b: r.b }], winner: r.a > r.b ? 'A' : 'B', endedBy: 'target' }, force: true });
    if (res.ok) void drop(r.id);
  };
  const ago = (at: number) => {
    const min = Math.max(0, Math.round((now - at) / 60_000));
    return min === 0 ? t('gerade eben') : t('vor {n} min', { n: min });
  };

  return (
    <div className="bg-panel border border-warn px-4 py-2.5 mb-3">
      <div className="label flex items-center gap-2 mb-2">
        <ClipboardCheck size={14} className="text-warn shrink-0" />
        {t('Ergebnismeldungen der Spieler')}
        <span className="ml-auto font-mono">{rows.length}</span>
      </div>
      <div className="grid gap-1.5">
        {rows.map(({ r, m }) => {
          const draw = r.a === r.b;
          return (
            <div key={r.id} className="flex items-center gap-3 flex-wrap text-sm border-t border-line2 pt-1.5 first:border-t-0 first:pt-0">
              <div className="min-w-0 flex-1">
                <div className="label truncate">
                  {matchContext(comp, m)} · {feldName(tournament.courts.find((c) => c.id === m.courtId)?.name ?? '')}
                </div>
                <div className="truncate">
                  <b>{slotLabel(m.a, lookup, allMatches, true)}</b> <span className="text-dim">vs</span> <b>{slotLabel(m.b, lookup, allMatches, true)}</b>
                </div>
              </div>
              <div className="font-mono text-[13px] whitespace-nowrap">
                <b className="text-[15px]">
                  {r.a}:{r.b}
                </b>
                <span className="text-dim"> · {r.by ? t('von {by} · {ago}', { by: r.by, ago: ago(r.createdAt) }) : ago(r.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="primary" disabled={draw} onClick={() => confirm(r, m)} title={draw ? t('Unentschieden — bitte über „Ergebnis“ eintragen') : t('Ergebnis übernehmen')}>
                  {t('Bestätigen')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void drop(r.id)}>
                  {t('Verwerfen')}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
